/**
 * useCrossPortalHooks.ts
 * 
 * Hooks React Query per la gestione delle comunicazioni cross-portal in Ufficio Stile
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { notifications } from '@mantine/notifications';
import {
  crossPortalService,
  AddGlobalCommentDto,
  UploadGlobalAttachmentDto
} from '../services/CrossPortalService';

// Hook per recuperare commenti globali
export const useGlobalComments = (requestId?: string | number) => {
  const id = requestId === undefined || requestId === null ? undefined : (typeof requestId === 'string' ? parseInt(requestId) : requestId);
  return useQuery({
    queryKey: ['global-comments', id],
    queryFn: () => crossPortalService.getGlobalComments(id as number),
    enabled: !!id,
    staleTime: 30000,
    refetchInterval: 30000,
  });
};

// Hook per aggiungere commento globale
export const useAddGlobalComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: AddGlobalCommentDto) => 
      crossPortalService.addGlobalComment(payload),
    onSuccess: (_, variables) => {
      // variables may use either crossPortalRequestId or requestId
      const id = (variables as any).crossPortalRequestId ?? (variables as any).requestId ?? undefined;
      queryClient.invalidateQueries({ 
        queryKey: ['global-comments', id] 
      });
      
      notifications.show({
        title: 'Commento condiviso',
        message: 'Il commento è stato condiviso con tutti i portali',
        color: 'green'
      });
    },
    onError: (error) => {
      notifications.show({
        title: 'Errore',
        message: 'Errore durante la condivisione del commento',
        color: 'red'
      });
      console.error('Failed to add global comment:', error);
    }
  });
};

// Hook per aggiungere commento interno
export const useAddInternalComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: AddGlobalCommentDto) => 
      crossPortalService.addInternalComment(payload),
    onSuccess: (_, variables) => {
      const id = (variables as any).crossPortalRequestId ?? (variables as any).requestId ?? undefined;
      queryClient.invalidateQueries({ 
        queryKey: ['comments', id] 
      });
    },
    onError: (error) => {
      notifications.show({
        title: 'Errore',
        message: 'Errore durante l\'aggiunta del commento',
        color: 'red'
      });
      console.error('Failed to add internal comment:', error);
    }
  });
};

// Hook per recuperare allegati globali
export const useGlobalAttachments = (requestId?: string | number) => {
  const id = requestId === undefined || requestId === null ? undefined : (typeof requestId === 'string' ? parseInt(requestId) : requestId);
  return useQuery({
    queryKey: ['global-attachments', id],
    queryFn: () => crossPortalService.getGlobalAttachments(id as number),
    enabled: !!id,
    staleTime: 30000,
    refetchInterval: 30000,
  });
};

// Hook per upload allegato globale
export const useUploadGlobalAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UploadGlobalAttachmentDto) => 
      crossPortalService.uploadGlobalAttachment(payload),
    onSuccess: (_, variables) => {
      const id = (variables as any).crossPortalRequestId ?? (variables as any).requestId ?? undefined;
      queryClient.invalidateQueries({ 
        queryKey: ['global-attachments', id] 
      });
      
      notifications.show({
        title: 'Allegato condiviso',
        message: 'L\'allegato è stato condiviso con tutti i portali',
        color: 'green'
      });
    },
    onError: (error) => {
      notifications.show({
        title: 'Errore',
        message: 'Errore durante l\'upload dell\'allegato',
        color: 'red'
      });
      console.error('Failed to upload global attachment:', error);
    }
  });
};

// Hook per sincronizzazione manuale
export const useCrossPortalSync = () => {
  const queryClient = useQueryClient();

  const syncData = useCallback(async () => {
    try {
      await queryClient.invalidateQueries({ 
        queryKey: ['global-comments'] 
      });
      await queryClient.invalidateQueries({ 
        queryKey: ['global-attachments'] 
      });
      
      notifications.show({
        title: 'Sincronizzazione completata',
        message: 'Dati aggiornati con successo',
        color: 'green'
      });
    } catch (error) {
      notifications.show({
        title: 'Errore',
        message: 'Errore durante la sincronizzazione',
        color: 'red'
      });
      console.error('Failed to sync cross-portal data:', error);
    }
  }, [queryClient]);

  return { syncData };
};

// Hook per download allegato globale
export const useDownloadGlobalAttachment = () => {
  return useMutation({
    mutationFn: (attachmentId: string) => 
      crossPortalService.getAttachmentDownloadUrl(attachmentId),
    onSuccess: (downloadUrl) => {
      const link = document.createElement('a');
      link.href = downloadUrl.url;
      link.download = downloadUrl.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onError: (error) => {
      notifications.show({
        title: 'Errore',
        message: 'Errore durante il download dell\'allegato',
        color: 'red'
      });
      console.error('Failed to download attachment:', error);
    }
  });
};
